import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getToken, getUser, setAuth } from "@/lib/api";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";

/** Forced first-login password change (dealer onboarding). */
export default function ResetPassword() {
  const navigate = useNavigate();
  const user = getUser();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pwd === "12345678") return toast.error("Please choose a different password than the temporary one.");
    if (pwd !== confirm) return toast.error("Passwords don't match.");
    setSaving(true);
    try {
      await api.post("/auth/force-reset", { new_password: pwd });
      setAuth(getToken(), { ...user, must_reset_password: false });
      toast.success("Password updated — welcome aboard!");
      navigate("/learn");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update the password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <Card className="p-8 border-slate-200" data-testid="reset-password-card">
          <div className="w-12 h-12 rounded-full bg-[#E53935]/10 flex items-center justify-center mb-4">
            <KeyRound size={20} className="text-[#E53935]" />
          </div>
          <h1 className="text-xl font-display font-bold text-[#222222]">Set your new password</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            Hi {(user?.name || "").split(" (")[0] || "there"} — you're signed in with a temporary password.
            Please choose a personal one to continue.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <input
                data-testid="reset-new-password-input"
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                placeholder="New password (min 8 characters)"
                className="w-full px-3 py-2.5 pr-10 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935]"
                autoFocus
              />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <input
              data-testid="reset-confirm-password-input"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935]"
            />
            <button
              type="submit"
              data-testid="reset-password-submit"
              disabled={saving || pwd.length < 8 || pwd !== confirm}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#222222] hover:bg-black disabled:opacity-50 text-white text-sm font-medium rounded-md transition-colors"
            >
              <ShieldCheck size={15} /> {saving ? "Saving…" : "Save and continue"}
            </button>
          </form>
        </Card>
        <p className="text-center text-[11px] text-slate-400 mt-4">UniVicoustic Learning Academy · Corporate L&D</p>
      </div>
    </div>
  );
}
